import React, { useState } from 'react';
import axios from 'axios';
import { UploadSimple, User } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (val) => {
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  return `${API_URL}${val}`;
};

/**
 * Reusable profile-photo picker for buyers/sellers. Calls POST /api/users/profile/upload-photo.
 * Props:
 *  - value: current photo URL or relative path
 *  - onChange(newUrl): fired after a successful upload OR when manual URL is edited / Remove clicked
 *  - size: avatar size in px (default 64)
 */
const ProfilePhotoUploader = ({ value, onChange, size = 64 }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/users/profile/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.profile_photo;
      if (url) {
        onChange?.(url);
        toast.success('Photo updated');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid="profile-photo-uploader">
      {value ? (
        <img src={photoSrc(value)} alt="Avatar"
          style={{ width: size, height: size }}
          className="rounded-full object-cover border border-slate-200"
          data-testid="profile-photo-preview" />
      ) : (
        <div style={{ width: size, height: size }} className="rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
          <User size={Math.floor(size * 0.45)} />
        </div>
      )}
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-300 text-slate-700 text-xs font-medium cursor-pointer bg-white hover:bg-slate-50" data-testid="upload-photo-label">
            <UploadSimple size={14} />
            {uploading ? 'Uploading…' : 'Upload Image'}
            <input type="file" accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              disabled={uploading} className="hidden" data-testid="upload-photo-input" />
          </label>
          {value && (
            <Button size="sm" variant="ghost" onClick={() => onChange?.('')}
              className="text-red-500 hover:bg-red-50 h-9 rounded-lg text-xs" data-testid="remove-photo-btn">
              Remove
            </Button>
          )}
        </div>
        <Input value={value || ''} onChange={(e) => onChange?.(e.target.value)}
          placeholder="Or paste an image URL..."
          className="h-9 text-sm" data-testid="profile-photo-url-input" />
      </div>
    </div>
  );
};

export default ProfilePhotoUploader;
